import { AppLauncher } from '@capacitor/app-launcher';
import { Share } from '@capacitor/share';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Device } from '@capacitor/device';
import html2canvas from 'html2canvas';
//import { Media } from '@capacitor-community/media';

const formatarNomeDiretorio = (nome) => {
  return nome
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^\w]/g, '');
};

const getDDDFromNumber = (rawNumber, dddPadrao = "11") => {
  let digits = rawNumber?.replace(/\D/g, '');
  if (!digits) return '';

  // remove DDI se existir
  if (digits.startsWith('55')) {
    digits = digits.slice(2);
  }

  // se não tiver DDD, retorna o padrão
  if (digits.length === 8 || digits.length === 9) {
    return dddPadrao;
  }

  // pega os dois primeiros dígitos como DDD
  return digits.slice(0, 2);
};

const normalizeNumber = (rawNumber, dddPadrao = "11") => {

  let digits = rawNumber?.replace(/\D/g, '');
  if (!digits) return '';

  // remove DDI se vier duplicado
  if (digits.startsWith('55') && digits.length > 12) {
    digits = digits.slice(2);
  }

  // se vier só o número (8 ou 9 dígitos), adiciona DDD padrão
  if (digits.length === 8 || digits.length === 9) {
    digits = dddPadrao + digits;
  }

  // se tiver 10 dígitos (DDD + número sem 9), adiciona o 9
  if (digits.length === 10) {
    const ddd = digits.slice(0, 2);
    const base = digits.slice(2);
    digits = ddd + '9' + base;
  }

  // adiciona DDI
  if (!digits.startsWith('55')) digits = '55' + digits;

  return digits;
};

const enviarWhatsApp = async (telefone, mensagemTexto, Toast, isNative) => {
  try {
    const telefoneLimpo = normalizeNumber(telefone);
    const mensagemEncoded = encodeURIComponent(mensagemTexto || '');
    
    const urlNative = `whatsapp://send?phone=${telefoneLimpo}&text=${mensagemEncoded}`;
    const urlWeb = `https://wa.me/${telefoneLimpo}?text=${mensagemEncoded}`;

    if (isNative) {
      const { value: canOpen } = await AppLauncher.canOpenUrl({ url: 'whatsapp://' });
      if (canOpen) {
        await AppLauncher.openUrl({ url: urlNative });
      } else {
        window.open(urlWeb, '_system');
      }
    } else {
      window.open(urlWeb, '_blank', 'noopener,noreferrer');
    }
  } catch (err) {
    console.error("Erro no WhatsApp:", err);
  }
};

const carregarConversaComImagem = async ({numero, texto, imagemUri, url}, Toast) => {
  try{
    const numeroFormatado = normalizeNumber(numero);
    if (!numeroFormatado) return;
    
    // Verifica se o plugin existe antes de chamar a função
    if (!window.plugins || !window.plugins.socialsharing) {
      Toast.fire({ icon: 'error', title: 'Plugin de compartilhamento não encontrado' });
      return;
    }
  
    const mensagemCompleta = url ? `${texto}\n\n${url}` : texto;
  
    window.plugins.socialsharing.shareViaWhatsAppToReceiver(
      numeroFormatado,
      mensagemCompleta,
      imagemUri,
      null,
      () => console.log('WhatsApp aberto com sucesso!'),
      (err) => {
        //alert(`Ero no plugin: ${err}`)
        console.error('Erro ao abrir WhatsApp:', err);
      }
    );
  }catch (err){
    //alert(`Erro carregarConversaComImagem: ${imagemUri} => ${err}`)
  }
};

// Função auxiliar para pedir permissão corretamente
const solicitarPermissaoAndroid = async () => {
  const info = await Device.getInfo();
  const androidVersion = parseInt(info.osVersion.split('.')[0]);
  let granted = false;

  if (androidVersion >= 13) {
    const status = await Filesystem.requestPermissions({ permissions: ['photos'] });
    granted = status.photos === 'granted';
  } else {
    const status = await Filesystem.requestPermissions({ permissions: ['publicStorage'] });
    granted = status.publicStorage === 'granted';
  }

  return { granted, androidVersion };
};

// Tenta salvar em múltiplos diretórios
const salvarEmDiretorios = async (fileName: string, base64Content: string, Toast, app_name) => {
  const dirs = [Directory.Downloads, Directory.Pictures, Directory.Documents];
  const path = `${formatarNomeDiretorio(app_name||"Comprovantes")}`;
  for (const dir of dirs) {
    try {
      const saved = await Filesystem.writeFile({
        path: `${path}/${fileName}`,
        data: base64Content,
        directory: dir,
        recursive: true
      });
      return saved.uri; // Retorna assim que salvar com sucesso
    } catch (e) {
      console.warn(`Falha ao salvar em ${dir}`, e);
    }
  }
  Toast.fire({ icon: 'error', title: `Não foi possível salvar a imagem em nenhum diretório.` });
  //throw new Error('Não foi possível salvar a imagem em nenhum diretório.');
};

// Compartilhar captura
const compartilharCaptura = async (divRef, { url, text, title, telefone }, Toast, isNative, app_name) => {
  try {
    const canvas = await html2canvas(divRef.current, { useCORS: true, allowTaint: true });
    const shareText = text || '';
    const telefoneLimpo = normalizeNumber(telefone);
    const fileName = `${formatarNomeDiretorio(title)}_${Date.now()}.png`;

    if (isNative) {
      const { granted, androidVersion } = await solicitarPermissaoAndroid();
      if (!granted) {
        Toast.fire({ icon: 'error', title: 'Permissão de armazenamento negada.' });
        return;
      }

      const base64Data = canvas.toDataURL('image/png');
      const base64Content = base64Data.split(',')[1];

      const uriFinal = await salvarEmDiretorios(fileName, base64Content, Toast, app_name);
      if (telefoneLimpo) {
        // Salva em Downloads/Pictures/Documents
        const texto = `${shareText}${url ? `\n\n${url}` : ''}`;
        await carregarConversaComImagem({ numero: telefoneLimpo, texto, imagemUri: uriFinal }, Toast);
      } else {
        // Salva em Temporário para Share
        //const savedFile = await Filesystem.writeFile({ path: fileName, data: base64Content, directory: Directory.Temporary, recursive: true });
        await Share.share({
          title: title || 'Comprovante',
          text: `${shareText}${url ? `\n\n${url}` : ''}`,
          url: uriFinal,
        });
      }

    } else {
      // PWA
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      const file = new File([blob], fileName, { type: 'image/png' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
    }

  } catch (error) {
    console.error(error);
    //Toast.fire({ icon: 'error', title: 'Erro ao processar captura.' });
  }
};

// Baixar captura
const baixarCaptura = async (divRef, { nome }, Toast, isNative, app_name) => {
  try {
    const canvas = await html2canvas(divRef.current, { useCORS: true, allowTaint: true });
    const base64Data = canvas.toDataURL('image/png');
    const base64Content = base64Data.split(',')[1];
    const fileName = `${formatarNomeDiretorio(nome)}_${Date.now()}.png`;

    if (isNative) {
      const { granted } = await solicitarPermissaoAndroid();
      if (!granted) {
        Toast.fire({ icon: 'error', title: 'Permissão de armazenamento negada.' });
        return;
      }

      await salvarEmDiretorios(fileName, base64Content, Toast, app_name);
      Toast.fire({ icon: 'success', title: `Download de ${nome || 'Imagem'} concluído.` });

    } else {
      const link = document.createElement('a');
      link.href = base64Data;
      link.download = fileName;
      link.click();
    }

  } catch (error) {
    console.error(error);
    //Toast.fire({ icon: 'error', title: 'Erro ao salvar imagem.' });
  }
};

export { getDDDFromNumber, normalizeNumber, enviarWhatsApp, compartilharCaptura, baixarCaptura };
export default { getDDDFromNumber, normalizeNumber, enviarWhatsApp, compartilharCaptura, baixarCaptura };
